// frontend/src/components/EditableProjectTitle.js - Updated
import React, { useState, useEffect, useRef } from 'react';
import editableTitleService from '../services/document/editableTitleService';
import '../components/EditableTitle.css'; // Reuse the same styles

const EditableProjectTitle = ({ 
  projectId, 
  initialTitle, 
  className = '', 
  onUpdate = null,
  fontSize = 'text-xl'
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'success', 'error'
  const inputRef = useRef(null);
  
  useEffect(() => {
    // Focus input when editing starts
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  useEffect(() => {
    // Reset save status after a delay
    if (saveStatus) {
      const timer = setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);
  
  const startEditing = (e) => {
    // Stop event propagation to prevent navigation or other parent handlers
    e.stopPropagation();
    setIsEditing(true);
  };
  
  const saveTitle = async () => {
    if (title.trim() === '') {
      setTitle(initialTitle);
      setIsEditing(false);
      return;
    }
    
    if (title === initialTitle) {
      setIsEditing(false);
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await editableTitleService.updateProjectTitle(projectId, title.trim());
      
      setSaveStatus('success');
      
      // Notify parent component if callback is provided
      if (onUpdate && typeof onUpdate === 'function') {
        onUpdate(response);
      }
    } catch (error) {
      console.error('Failed to update project title:', error);
      setSaveStatus('error');
      setTitle(initialTitle); // Revert to original title on error
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setTitle(initialTitle);
      setIsEditing(false);
    }
  };
  
  const getStatusClass = () => {
    if (!saveStatus) return '';
    return saveStatus === 'success' ? 'save-success' : 'save-error';
  };

  const handleInputClick = (e) => {
    // Prevent click from propagating to parent elements
    e.stopPropagation();
  };
  
  return (
    <div className={`editable-title-container ${className} ${getStatusClass()}`} onClick={(e) => e.stopPropagation()}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={handleKeyDown}
          onClick={handleInputClick}
          className={`editable-title-input ${fontSize}`}
          disabled={isSaving}
        />
      ) : (
        <h3
          onClick={startEditing}
          className={`editable-title ${fontSize} ${getStatusClass()}`}
        >
          {title}
          <span className="edit-icon">✎</span>
        </h3>
      )}
    </div>
  );
};

export default EditableProjectTitle;